import React from 'react';
import { TrendingUp, BarChart3, Target, Bot, Copy, Briefcase, X, Grid3X3 } from 'lucide-react';
import { CanvasWidget } from './types';

interface TemplatesPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (templateType: string) => void;
  position?: { x: number; y: number };
}

const templates = [
  {
    id: 'token-trading',
    title: 'Token Trading',
    description: 'Set up trading charts, order books, and position management',
    icon: TrendingUp,
    color: '#24c35b'
  },
  {
    id: 'token-analysis',
    title: 'Token Analysis',
    description: 'Comprehensive token analysis with charts and metrics',
    icon: BarChart3,
    color: '#4f9eff'
  },
  {
    id: 'sniper-setup',
    title: 'Sniper Setup',
    description: 'Quick entry tools for new token launches',
    icon: Target,
    color: '#ff6b6b'
  },
  {
    id: 'auto-trading-setup',
    title: 'Auto Trading Setup',
    description: 'Automated trading strategies and configuration',
    icon: Bot,
    color: '#ffd93d'
  },
  {
    id: 'copy-trading-setup',
    title: 'Copy Trading Setup',
    description: 'Follow and copy successful trader strategies',
    icon: Copy,
    color: '#a855f7'
  },
  {
    id: 'trading-bots',
    title: 'Trading Bots',
    description: 'All trading bots with P&L and position tracking',
    icon: Briefcase,
    color: '#06b6d4'
  },
  {
    id: 'all-widgets',
    title: 'All Widgets',
    description: 'Complete widget showcase with all available tools',
    icon: Grid3X3,
    color: '#f59e0b'
  }
];

const TemplatesPopup: React.FC<TemplatesPopupProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  position = { x: 0, y: 0 }
}) => {


  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - Block canvas interactions but allow popup interactions */}
      <div 
        className="fixed inset-0 z-[60]"
        onClick={onClose}
      />
      
      {/* Popup Container - Compact design near button */}
      <div 
        className="fixed z-[70]"
        style={{
          bottom: '80px',
          right: '120px',
          width: '280px',
          maxHeight: '400px'
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      >
        {/* Compact widget structure */}
        <div 
          className="w-full flex flex-col bg-white border border-gray-200/50 rounded-lg overflow-hidden shadow-lg"
          style={{
            backdropFilter: "blur(12px)",
            boxShadow: "0 4px 16px rgba(36, 195, 91, 0.15)"
          }}
        >
          {/* Compact Header */}
          <div className="p-2 border-b border-gray-200/50 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#24c35b] flex-shrink-0"></div>
              <h2 className="text-sm font-bold text-gray-900 leading-none">Templates</h2>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-900 transition-colors flex items-center justify-center"
              style={{ fontSize: '14px', lineHeight: '1' }}
            >
              ×
            </button>
          </div>

          {/* Compact Content Area */}
          <div 
            className="overflow-y-auto px-2 py-1" 
            style={{ background: 'white', maxHeight: '340px' }}
          >
            <div className="space-y-1">
              {templates.map((template) => {
                const IconComponent = template.icon;
                return (
                  <button
                    key={template.id}
                    onClick={() => onSelectTemplate(template.id)}
                    className="w-full p-2 rounded border border-gray-200/30 bg-gray-50/50 hover:bg-gray-100/70 hover:border-[#24c35b]/30 transition-all duration-200 text-left group"
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center border border-gray-200/50"
                        style={{ backgroundColor: `${template.color}15` }}
                      >
                        <IconComponent 
                          size={12} 
                          style={{ color: template.color }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 
                          className="text-xs font-medium group-hover:text-gray-900 transition-colors"
                          style={{ color: template.color }}
                        >
                          {template.title}
                        </h4>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Compact Footer */}
            <div className="mt-2 p-1.5 rounded bg-gray-50/70 border border-gray-200/30">
              <p className="text-[10px] text-gray-500 text-center">
                Select template to add widgets
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TemplatesPopup; 